import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Card } from "./ui/card.js";
import { SearchInput } from "./ui/search-input.js";
import { SelectField } from "./ui/select-field.js";
import { Pagination } from "./ui/pagination.js";
import { CompVerificationPanel } from "./CompVerificationPanel.js";
import { COMP_STATUS_BADGE, asNum, fmtAmt, fmtDate, humanize, type CompStatus } from "../lib/compensation.js";
import { usePaged } from "../lib/usePaged.js";
import { cn } from "../lib/utils.js";

interface Employee { firstName: string; lastName: string; employeeCode: string | null; designation: string | null }

/** GET /compensation/verifications — waiting for Finance */
interface PendingRevision {
  id:            string;
  employeeId:    string;
  ctcAnnual:     number | string;
  effectiveFrom: string;
  reason:        string;
  submittedAt:   string | null;
  employee:      Employee;
  previous:      { id: string; ctcAnnual: number | string } | null;
}

/** GET /compensation/verifications/all — every request sent to Finance, with its outcome */
interface RequestRow extends PendingRevision {
  status:          CompStatus;
  submittedByName: string | null;
  decidedAt:       string | null;
  decidedByName:   string | null;
  decisionComment: string | null;
}

function ctcChange(p: PendingRevision): { text: string; up: boolean } | null {
  if (!p.previous) return null;
  const before = asNum(p.previous.ctcAnnual);
  if (before === 0) return null;
  const pct = ((asNum(p.ctcAnnual) - before) / before) * 100;
  return { text: `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`, up: pct >= 0 };
}

const matches = (e: Employee, q: string) =>
  !q || [e.firstName, e.lastName, `${e.firstName} ${e.lastName}`, e.employeeCode, e.designation].some(v => v?.toLowerCase().includes(q));

/** Shared CTC cells: previous, requested, change. */
function CtcCells({ r }: { r: PendingRevision }) {
  const change = ctcChange(r);
  return (
    <>
      <TableCell className="px-3 text-right tabular-nums">{r.previous ? `₹${fmtAmt(asNum(r.previous.ctcAnnual))}` : "—"}</TableCell>
      <TableCell className="px-3 text-right font-semibold tabular-nums">₹{fmtAmt(asNum(r.ctcAnnual))}</TableCell>
      <TableCell className={cn("px-3 text-right tabular-nums", change && (change.up ? "text-[var(--color-success-fg)]" : "text-[var(--color-error-fg)]"))}>
        {change?.text ?? "New"}
      </TableCell>
    </>
  );
}

function EmployeeCell({ e }: { e: Employee }) {
  return (
    <TableCell className="px-3">
      <div className="font-semibold">{e.firstName} {e.lastName}</div>
      <div className="text-[11px] text-surface-500">{[e.employeeCode, e.designation].filter(Boolean).join(" · ")}</div>
    </TableCell>
  );
}

/** Finance's compensation requests: Pending (to verify) and All requests (every outcome). */
export function CompVerificationQueue() {
  const [tab, setTab]           = useState<"pending" | "all">("pending");
  const [rows, setRows]         = useState<PendingRevision[]>([]);
  const [all, setAll]           = useState<RequestRow[] | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [selected, setSelected] = useState<PendingRevision | null>(null);

  const loadPending = useCallback(async () => {
    try {
      const res = await api.get<PendingRevision[]>("/compensation/verifications");
      setRows(res.data); setError(null);
    } catch (e) {
      logError("CompVerificationQueue.load", e);
      setError(errMsg(e) ?? "Couldn't load the verification queue.");
    } finally { setLoading(false); }
  }, []);
  const loadAll = useCallback(async () => {
    try {
      const res = await api.get<RequestRow[]>("/compensation/verifications/all");
      setAll(res.data);
    } catch (e) {
      logError("CompVerificationQueue.loadAll", e);
      setError(errMsg(e) ?? "Couldn't load all requests.");
    }
  }, []);

  useEffect(() => { void loadPending(); }, [loadPending]);
  // All requests loads the first time it is opened.
  useEffect(() => { if (tab === "all" && all === null) void loadAll(); }, [tab, all, loadAll]);

  // ── Pending: search (name, code, designation) + reason, 20 per page ──
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("");
  const reasons = useMemo(() => [...new Set(rows.map(r => r.reason))], [rows]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => (!reason || r.reason === reason) && matches(r.employee, q));
  }, [rows, search, reason]);
  const pages = usePaged(visible, `${search.trim()}|${reason}`);

  // ── All requests: search + status + reason, 20 per page ──
  const [aSearch, setASearch] = useState("");
  const [aStatus, setAStatus] = useState("");
  const [aReason, setAReason] = useState("");
  const aReasons = useMemo(() => [...new Set((all ?? []).map(r => r.reason))], [all]);
  const aVisible = useMemo(() => {
    const q = aSearch.trim().toLowerCase();
    return (all ?? []).filter(r => (!aStatus || r.status === aStatus) && (!aReason || r.reason === aReason) && matches(r.employee, q));
  }, [all, aSearch, aStatus, aReason]);
  const aPages = usePaged(aVisible, `${aSearch.trim()}|${aStatus}|${aReason}`);

  const selectedStatus = (all ?? []).find(r => r.id === selected?.id)?.status ?? "PENDING_VERIFICATION";
  const selectedIsPending = rows.some(r => r.id === selected?.id);

  if (loading) return <p className="text-sm text-surface-400">Loading…</p>;

  return (
    <>
      {error && <p className="mb-3 text-sm text-[var(--color-error-fg)]">{error}</p>}
      <Tabs value={tab} onValueChange={v => setTab(v as "pending" | "all")}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending">Pending ({rows.length})</TabsTrigger>
          <TabsTrigger value="all">All requests{all ? ` (${all.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {rows.length === 0 ? (
            <p className="text-sm text-surface-400">No compensation revisions are waiting for verification.</p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <SearchInput className="w-72" placeholder="Search name, employee code or designation" aria-label="Search revisions"
                  value={search} onChange={e => setSearch(e.target.value)} />
                <SelectField size="sm" className="w-52" value={reason} onChange={e => setReason(e.target.value)} aria-label="Reason">
                  <option value="">All reasons</option>
                  {reasons.map(r => <option key={r} value={r}>{humanize(r)}</option>)}
                </SelectField>
              </div>
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Current CTC</TableHead>
                      <TableHead className="text-right">Proposed CTC</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                      <TableHead>Effective</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visible.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="px-3 py-6 text-center text-surface-400">No revisions match these filters.</TableCell>
                      </TableRow>
                    )}
                    {pages.pageItems.map(r => (
                      <TableRow key={r.id}>
                        <EmployeeCell e={r.employee} />
                        <TableCell className="px-3">{humanize(r.reason)}</TableCell>
                        <CtcCells r={r} />
                        <TableCell className="px-3">{fmtDate(r.effectiveFrom)}</TableCell>
                        <TableCell className="px-3">{r.submittedAt ? fmtDate(r.submittedAt) : "—"}</TableCell>
                        <TableCell className="px-3 text-right">
                          <Button type="button" size="sm" variant="outline" onClick={() => setSelected(r)}>Review</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
              <Pagination meta={pages.meta} onPageChange={pages.setPage} />
            </>
          )}
        </TabsContent>

        <TabsContent value="all">
          {all === null ? (
            <p className="text-sm text-surface-400">Loading…</p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <SearchInput className="w-72" placeholder="Search name, employee code or designation" aria-label="Search all requests"
                  value={aSearch} onChange={e => setASearch(e.target.value)} />
                <SelectField size="sm" className="w-48" value={aStatus} onChange={e => setAStatus(e.target.value)} aria-label="Status">
                  <option value="">All statuses</option>
                  <option value="PENDING_VERIFICATION">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                </SelectField>
                <SelectField size="sm" className="w-52" value={aReason} onChange={e => setAReason(e.target.value)} aria-label="Reason">
                  <option value="">All reasons</option>
                  {aReasons.map(r => <option key={r} value={r}>{humanize(r)}</option>)}
                </SelectField>
              </div>
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Previous CTC</TableHead>
                      <TableHead className="text-right">Requested CTC</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                      <TableHead>Effective</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Decided</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aVisible.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="px-3 py-6 text-center text-surface-400">No requests match these filters.</TableCell>
                      </TableRow>
                    )}
                    {aPages.pageItems.map(r => {
                      const badge = COMP_STATUS_BADGE[r.status];
                      return (
                        <TableRow key={r.id} className="align-top">
                          <EmployeeCell e={r.employee} />
                          <TableCell className="px-3">{humanize(r.reason)}</TableCell>
                          <CtcCells r={r} />
                          <TableCell className="px-3">{fmtDate(r.effectiveFrom)}</TableCell>
                          <TableCell className="px-3 text-[12px]">
                            {r.submittedAt ? fmtDate(r.submittedAt) : "—"}
                            {r.submittedByName && <div className="text-surface-500">{r.submittedByName}</div>}
                          </TableCell>
                          <TableCell className="px-3">{badge && <Badge tone={badge.tone}>{r.status === "PENDING_VERIFICATION" ? "Pending" : badge.label}</Badge>}</TableCell>
                          <TableCell className="px-3 text-[12px]">
                            {r.decidedAt ? <>{fmtDate(r.decidedAt)}<div className="text-surface-500">{r.decidedByName ?? "—"}</div></> : "—"}
                            {r.decisionComment && <div className="max-w-56 text-surface-500">“{r.decisionComment}”</div>}
                          </TableCell>
                          <TableCell className="px-3 text-right">
                            <Button type="button" size="sm" variant="outline" onClick={() => setSelected(r)}>
                              {r.status === "PENDING_VERIFICATION" ? "Review" : "View"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
              <Pagination meta={aPages.meta} onPageChange={aPages.setPage} />
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={o => { if (!o) setSelected(null); }}>
        {/* 85% of the screen wide, 80% tall, so the tiles and the yearly/monthly compare table fit;
            content starts at the top and scrolls inside when longer. */}
        <DialogContent className="h-[80vh] max-h-[80vh] w-[95vw] max-w-[95vw] content-start sm:w-[85vw] sm:max-w-[85vw]">
          <DialogHeader>
            <DialogTitle>
              {selectedIsPending || selectedStatus === "PENDING_VERIFICATION" ? "Verify compensation" : "Compensation request"}
              {selected && ` — ${selected.employee.firstName} ${selected.employee.lastName}`}
            </DialogTitle>
            <DialogDescription>
              {selectedIsPending || selectedStatus === "PENDING_VERIFICATION"
                ? "Check the breakdown against the current verified figures, then approve or send it back to HR."
                : "The request as it was sent to Finance, compared with the verified figures before it."}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <CompVerificationPanel
              employeeId={selected.employeeId}
              snapshotId={selected.id}
              baselineId={selected.previous?.id ?? null}
              isVerifier
              canEditComp={false}
              // A decision moves the request from Pending to All requests: refresh both lists.
              onChanged={() => { setSelected(null); void loadPending(); void loadAll(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
